/**
 * Experten-Profil API
 *
 * Kommuniziert mit dem expert-profile-service (Port 3100).
 * Wird ueber Nginx transparent auf /api/v1/expert-profile/* proxied.
 */
import api from '../api/client';
import type {
  ExpertProfile,
  ExpertExperience,
  ExpertLanguage,
  ExpertProject,
  ExpertCertification,
  ExpertAttachment,
} from './types';

const BASE = '/expert-profile';

// ─── Eigenes Profil ───────────────────────────────────────────────

export async function getMyProfile(): Promise<ExpertProfile> {
  const { data } = await api.get(`${BASE}/me`);
  return data;
}

export async function updateMySkills(skills: string[]): Promise<ExpertProfile> {
  const { data } = await api.patch(`${BASE}/me/skills`, { skills });
  return data;
}

// --- Erfahrung ---
export async function addMyExperience(dto: {
  area: string;
  years: number;
  level?: string;
}): Promise<ExpertExperience> {
  const { data } = await api.post(`${BASE}/me/experiences`, dto);
  return data;
}

export async function deleteMyExperience(id: string): Promise<void> {
  await api.delete(`${BASE}/me/experiences/${id}`);
}

// --- Sprachen ---
export async function addMyLanguage(dto: {
  language: string;
  level: string;
}): Promise<ExpertLanguage> {
  const { data } = await api.post(`${BASE}/me/languages`, dto);
  return data;
}

export async function deleteMyLanguage(id: string): Promise<void> {
  await api.delete(`${BASE}/me/languages/${id}`);
}

// --- Projekte ---
export async function addMyProject(dto: Partial<ExpertProject>): Promise<ExpertProject> {
  const { data } = await api.post(`${BASE}/me/projects`, dto);
  return data;
}

export async function updateMyProject(
  id: string,
  dto: Partial<ExpertProject>,
): Promise<ExpertProject> {
  const { data } = await api.patch(`${BASE}/me/projects/${id}`, dto);
  return data;
}

export async function deleteMyProject(id: string): Promise<void> {
  await api.delete(`${BASE}/me/projects/${id}`);
}

// --- Zertifikate ---
export async function addMyCertification(dto: {
  title: string;
  issuingBody: string;
  website?: string;
  issueYear: number;
}): Promise<ExpertCertification> {
  const { data } = await api.post(`${BASE}/me/certifications`, dto);
  return data;
}

export async function updateMyCertification(
  id: string,
  dto: Partial<ExpertCertification>,
): Promise<ExpertCertification> {
  const { data } = await api.patch(`${BASE}/me/certifications/${id}`, dto);
  return data;
}

export async function deleteMyCertification(id: string): Promise<void> {
  await api.delete(`${BASE}/me/certifications/${id}`);
}

// --- Anhaenge ---
export async function uploadMyAttachment(dto: {
  filename: string;
  mimetype: string;
  size: number;
  data: string;
}): Promise<ExpertAttachment> {
  const { data } = await api.post(`${BASE}/me/attachments`, dto);
  return data;
}

export async function downloadMyAttachment(
  id: string,
): Promise<{ filename: string; mimetype: string; data: string }> {
  const { data } = await api.get(`${BASE}/me/attachments/${id}`);
  return data;
}

export async function deleteMyAttachment(id: string): Promise<void> {
  await api.delete(`${BASE}/me/attachments/${id}`);
}

// --- Veröffentlichen ---
export async function setMyPublished(isPublished: boolean): Promise<{ id: string; isPublished: boolean }> {
  const { data } = await api.patch(`${BASE}/me/publish`, { isPublished });
  return data;
}

// --- Export ---
export function getMyExportPdfUrl(): string {
  return `${BASE}/me/export/pdf`;
}

export function getMyExportDocxUrl(): string {
  return `${BASE}/me/export/docx`;
}

// ─── Alle Profile (Lesen) ─────────────────────────────────────────

export async function getAllProfiles(): Promise<ExpertProfile[]> {
  const { data } = await api.get(`${BASE}/users`);
  return data;
}

export async function getUserProfile(userId: string): Promise<ExpertProfile> {
  const { data } = await api.get(`${BASE}/users/${userId}`);
  return data;
}

// --- Export anderer User ---
export function getUserExportPdfUrl(userId: string): string {
  return `${BASE}/users/${userId}/export/pdf`;
}

export function getUserExportDocxUrl(userId: string): string {
  return `${BASE}/users/${userId}/export/docx`;
}

// ─── Admin: Andere Profile bearbeiten ─────────────────────────────

export async function adminUpdateSkills(
  userId: string,
  skills: string[],
): Promise<ExpertProfile> {
  const { data } = await api.patch(`${BASE}/users/${userId}/skills`, { skills });
  return data;
}

export async function adminAddExperience(
  userId: string,
  dto: { area: string; years: number; level?: string },
): Promise<ExpertExperience> {
  const { data } = await api.post(`${BASE}/users/${userId}/experiences`, dto);
  return data;
}

export async function adminDeleteExperience(userId: string, id: string): Promise<void> {
  await api.delete(`${BASE}/users/${userId}/experiences/${id}`);
}

export async function adminAddLanguage(
  userId: string,
  dto: { language: string; level: string },
): Promise<ExpertLanguage> {
  const { data } = await api.post(`${BASE}/users/${userId}/languages`, dto);
  return data;
}

export async function adminDeleteLanguage(userId: string, id: string): Promise<void> {
  await api.delete(`${BASE}/users/${userId}/languages/${id}`);
}

export async function adminAddProject(
  userId: string,
  dto: Partial<ExpertProject>,
): Promise<ExpertProject> {
  const { data } = await api.post(`${BASE}/users/${userId}/projects`, dto);
  return data;
}

export async function adminUpdateProject(
  userId: string,
  id: string,
  dto: Partial<ExpertProject>,
): Promise<ExpertProject> {
  const { data } = await api.patch(`${BASE}/users/${userId}/projects/${id}`, dto);
  return data;
}

export async function adminDeleteProject(userId: string, id: string): Promise<void> {
  await api.delete(`${BASE}/users/${userId}/projects/${id}`);
}

export async function adminAddCertification(
  userId: string,
  dto: { title: string; issuingBody: string; website?: string; issueYear: number },
): Promise<ExpertCertification> {
  const { data } = await api.post(`${BASE}/users/${userId}/certifications`, dto);
  return data;
}

export async function adminUpdateCertification(
  userId: string,
  id: string,
  dto: Partial<ExpertCertification>,
): Promise<ExpertCertification> {
  const { data } = await api.patch(`${BASE}/users/${userId}/certifications/${id}`, dto);
  return data;
}

export async function adminDeleteCertification(userId: string, id: string): Promise<void> {
  await api.delete(`${BASE}/users/${userId}/certifications/${id}`);
}
